// By default both popups share one lock, so showing either silences the other;
// "Run the popups independently" gives each trigger its own. A `session` lock
// lives in sessionStorage, a `day` lock in localStorage so it survives reloads.
const KEY = "7f_popup_shown";
const DAY_MS = 24 * 60 * 60 * 1000;

const storageFor = (frequency) => {
  try {
    return frequency === "day" ? window.localStorage : window.sessionStorage;
  } catch (error) {
    // Throws in private mode / with cookies blocked.
    return null;
  }
};

const lockKey = (trigger, independent) =>
  independent ? `${KEY}_${trigger}` : KEY;

const markShown = (trigger, frequency, independent = false) => {
  // "always" deliberately leaves no lock behind.
  if (frequency === "always") return;

  const store = storageFor(frequency);
  if (!store) return;

  try {
    store.setItem(
      lockKey(trigger, independent),
      JSON.stringify({ at: Date.now(), trigger })
    );
  } catch (error) {
    /* the popup will simply show again */
  }
};

// `null` = free to fire. Otherwise the ms left before it may fire again
// (`Infinity` = not again this session). `reopenAfterMinutes` shortens it.
const blockedFor = (
  trigger,
  frequency,
  reopenAfterMinutes = 0,
  independent = false
) => {
  if (frequency === "always") return null;

  const store = storageFor(frequency);
  if (!store) return null;

  let lock = null;
  try {
    const raw = store.getItem(lockKey(trigger, independent));
    lock = raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
  if (!lock || !Number(lock.at)) return null;

  const frequencyWindow = frequency === "day" ? DAY_MS : Infinity;
  const reopenWindow =
    reopenAfterMinutes > 0 ? reopenAfterMinutes * 60 * 1000 : Infinity;
  const wait = Math.min(frequencyWindow, reopenWindow) - (Date.now() - lock.at);

  return wait > 0 ? wait : null;
};

// Drops every lock, shared and per-trigger, from both stores (?popup=reset).
const clearLocks = () => {
  const keys = [KEY, `${KEY}_exit-intent`, `${KEY}_inactivity`];

  [storageFor("session"), storageFor("day")].forEach((store) => {
    if (!store) return;
    keys.forEach((key) => {
      try {
        store.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    });
  });
};

export { markShown, blockedFor, clearLocks };
